import PropTypes from 'prop-types'
import {useState, useEffect, useRef} from 'react'
import {getAddressInput} from '@/lib/address'
import {onInputFocus} from '@/lib/inputFocus'
import {POSTALCODE_LENGTH, getInputMaskCep} from '@/lib/format'
import {FORMATED_MIN_PRICE} from '@/lib/values'
import logger from '@/lib/logger'
import Button from '@/components/form/Button'
import SharedButton from '@/components/shared/Button'
import StaticMap from '@/components/shared/StaticMap'
import AddressAutoComplete from '@emcasa/places-autocomplete/AddressAutoComplete'
import CurrencyInput from '@/components/form/CurrencyInput'
import styles from './styles.module.css'
import composesStyles from '@/styles/_settings/composes.module.css'
import labelStyles from '@/styles/atoms/Label/styles.module.css'
import buttonStyles from '@/styles/atoms/Button/styles.module.css'
import inputStyles from '@/styles/atoms/Input/styles.module.css'

const Listing = ({
  state,
  onChange,
  onSubmit,
  onAddressFocus,
  loggerAction,
  hasPrice
}) => {
  const refAddressAutoComplete = useRef(null)
  const [focused, setFocus] = useState(false)
  const [loggerInteraction, setInteraction] = useState({
    types: null,
    address: null,
    addressData: null,
    streetNumber: null,
    postalCode: null,
    complement: null,
    price: null
  })

  const stateIncludesType = (type) => state.types.includes(type)

  const showStreetNumberInputWarning =
    !state.streetNumber || state.streetNumber === ''
  const showPostalCodeInputWarning =
    !state.postalCode ||
    (state.postalCode && state.postalCode.length < POSTALCODE_LENGTH)

  function onChangeTypes(value) {
    logger.action(loggerAction.listTypes, {types: [value]})
    onChange({
      ...state,
      types: [value]
    })
    if (!loggerInteraction.types) {
      setInteraction({
        ...loggerInteraction,
        types: true
      })
    }
  }

  function streetNumberAndPostalCodeLogs(
    streetNumberNotFound,
    postalCodeNotFound
  ) {
    if (
      streetNumberNotFound === undefined
        ? state.streetNumberNotFound
        : streetNumberNotFound
    ) {
      logger.action(loggerAction.streetNumberNotFound, {
        address: state.address ? state.address : null
      })
    }
    if (
      postalCodeNotFound === undefined
        ? state.postalCodeNotFound
        : postalCodeNotFound
    ) {
      logger.action(loggerAction.postalCodeNotFound, {
        address: state.address ? state.address : null
      })
    }
  }

  function addressInteraction(value) {
    if (!loggerInteraction.address) {
      setInteraction({
        ...loggerInteraction,
        address: true
      })
      logger.action(loggerAction.addressFocus, {value})
    }
  }

  function onBlurAddress(value) {
    if (value.length < 1) {
      onChange({
        ...state,
        address: null,
        addressData: null,
        streetNumber: null,
        streetNumberNotFound: false,
        postalCode: null,
        postalCodeNotFound: false
      })
    }
  }

  function onChangeAddress({
    address,
    addressData,
    city,
    addressState,
    neighborhood,
    streetNumber,
    streetNumberNotFound,
    postalCode,
    postalCodeNotFound
  }) {
    onChange({
      ...state,
      address,
      addressData,
      city,
      state: addressState,
      neighborhood,
      streetNumber: streetNumberNotFound ? null : streetNumber,
      streetNumberNotFound,
      postalCode: postalCodeNotFound ? null : postalCode,
      postalCodeNotFound
    })

    streetNumberAndPostalCodeLogs(streetNumberNotFound, postalCodeNotFound)

    if (!loggerInteraction.addressData) {
      setInteraction({
        ...loggerInteraction,
        addressData: true
      })
      logger.action(loggerAction.addressData, {
        address,
        addressData,
        city,
        state: addressState,
        neighborhood,
        streetNumber,
        streetNumberNotFound,
        postalCode,
        postalCodeNotFound
      })
    }
  }

  function onChangeComplement(value) {
    onChange({
      ...state,
      complement: value
    })
    if (!loggerInteraction.complement) {
      setInteraction({
        ...loggerInteraction,
        complement: true
      })
      logger.action(loggerAction.listingComplement, {
        complement: value
      })
    }
  }

  function onChangeStreetNumber(value) {
    onChange({
      ...state,
      streetNumber: value
    })
    if (!loggerInteraction.streetNumber) {
      setInteraction({
        ...loggerInteraction,
        streetNumber: true
      })
      logger.action(loggerAction.streetNumber, {
        streetNumber: value
      })
    }
  }

  function onChangePostalCode(value) {
    onChange({
      ...state,
      postalCode: getInputMaskCep(value)
    })
    if (!loggerInteraction.postalCode) {
      setInteraction({
        ...loggerInteraction,
        postalCode: true
      })
      logger.action(loggerAction.postalCode, {
        postalCode: getInputMaskCep(value)
      })
    }
  }

  function onChangePrice(value) {
    onChange({
      ...state,
      price: value
    })
    if (!loggerInteraction.price) {
      setInteraction({
        ...loggerInteraction,
        price: true
      })
      logger.action(loggerAction.listingPrice, {price: value})
    }
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (onSubmit) {
      onSubmit()
    }
  }

  useEffect(() => {
    streetNumberAndPostalCodeLogs()
  }, [])

  return (
    <div
      className={`${composesStyles.animation_enterfrombottom} ${composesStyles.paddingx} ${composesStyles.fullmaxwidth}`}
    >
      {state && (
        <>
          <div
            className={`${labelStyles.ecLabel} ${labelStyles.ecLabel_formItem}`}
          >
            <div className={labelStyles.ecLabel_formItem}>
              <StaticMap
                location={
                  state.addressData && state.addressData.geometry.location
                }
                icon={state.mapIcon}
                placeholder={state.mapPlaceholder}
              />
            </div>
            <span className={labelStyles.ecLabel__value}>Tipo de Imóvel</span>
            <div className={buttonStyles.ecButtonWrapper__ui}>
              <Button.Group initialValue={state.types} onChange={onChangeTypes}>
                <SharedButton
                  selected={stateIncludesType('Casa')}
                  small
                  value={'Casa'}
                >
                  Casa
                </SharedButton>
                <SharedButton
                  selected={stateIncludesType('Apartamento')}
                  small
                  value={'Apartamento'}
                >
                  Apartamento
                </SharedButton>
                <SharedButton
                  selected={stateIncludesType('Cobertura')}
                  small
                  value={'Cobertura'}
                >
                  Cobertura
                </SharedButton>
              </Button.Group>
            </div>
          </div>
          <label
            ref={refAddressAutoComplete}
            className={`${labelStyles.ecLabel} ${labelStyles.ecLabel_formItem}`}
          >
            <span className={labelStyles.ecLabel__value}>Endereço</span>
            <div className={inputStyles.ecAddressWrapper__ui}>
              <AddressAutoComplete
                p={0}
                height="medium"
                placeholder="Ex: Rua Redentor, 138 - Ipanema, Rio de Janeiro - RJ, Brasil*"
                icon={null}
                inputProps={{
                  p: 0,
                  tabIndex: 0,
                  'aria-label': 'Endereço e número do imóvel',
                  fontSize: '1rem',
                  onFocus: (e) => {
                    setFocus(true)
                    onInputFocus(refAddressAutoComplete.current.offsetTop)
                    addressInteraction(e.target.value)
                    if (onAddressFocus) {
                      onAddressFocus(refAddressAutoComplete.current)
                    }
                  },
                  onBlur: (e) => {
                    setFocus(false)
                    onBlurAddress(e.target.value)
                  }
                }}
                initialValue={state.address ? state.address : null}
                onSelect={(_, addressData, address) => {
                  const input = getAddressInput({addressData})
                  onChangeAddress({
                    address,
                    addressData,
                    city: input.city,
                    addressState: input.state,
                    neighborhood: input.neighborhood,
                    streetNumber: input.streetNumber,
                    streetNumberNotFound: input.streetNumber === undefined,
                    postalCode: input.postalCode,
                    postalCodeNotFound:
                      (input.postalCode &&
                        input.postalCode.length < POSTALCODE_LENGTH) ||
                      input.postalCode === undefined
                  })
                }}
              />
            </div>
          </label>
          <form
            onSubmit={handleSubmit}
            className={focused ? styles.ecListingAddress__form_focused : null}
          >
            {state.streetNumberNotFound && (
              <>
                <label
                  className={`${labelStyles.ecLabel} ${labelStyles.ecLabel_formItem}`}
                >
                  <span className={labelStyles.ecLabel__value}>
                    Número da rua do imóvel
                  </span>
                  <input
                    className={inputStyles.ecInput}
                    type="number"
                    name="streetNumber"
                    placeholder="Ex: 1234"
                    tabIndex="0"
                    value={state.streetNumber || ''}
                    onChange={(e) => onChangeStreetNumber(e.target.value)}
                    onFocus={(e) => onInputFocus(e.target.offsetTop)}
                  />
                  {showStreetNumberInputWarning ? (
                    <span className={labelStyles.ecLabel__warning}>
                      Não conseguimos localizar o endereço completo. Por favor,
                      confirme o número da rua do imóvel.
                    </span>
                  ) : null}
                </label>
              </>
            )}
            {state.postalCodeNotFound && (
              <>
                <label
                  className={`${labelStyles.ecLabel} ${labelStyles.ecLabel_formItem}`}
                >
                  <span className={labelStyles.ecLabel__value}>CEP</span>
                  <input
                    className={inputStyles.ecInput}
                    type="text"
                    name="postalCode"
                    placeholder="Ex: 00100-010"
                    tabIndex="0"
                    value={state.postalCode || ''}
                    onChange={({target}) => onChangePostalCode(target.value)}
                    onFocus={(e) => onInputFocus(e.target.offsetTop)}
                  />
                  {showPostalCodeInputWarning ? (
                    <span className={labelStyles.ecLabel__warning}>
                      Não conseguimos localizar o endereço completo. Por favor,
                      informe o número do CEP.
                    </span>
                  ) : null}
                </label>
              </>
            )}
            <label
              className={`${labelStyles.ecLabel} ${labelStyles.ecLabel_formItem}`}
            >
              <span className={labelStyles.ecLabel__value}>Complemento</span>
              <input
                className={inputStyles.ecInput}
                type="text"
                name="complement"
                tabIndex="0"
                placeholder="Ex: Apto 10"
                value={state.complement || undefined}
                onChange={({target}) => onChangeComplement(target.value)}
                onFocus={(e) => onInputFocus(e.target.offsetTop)}
              />
            </label>
            {hasPrice && (
              <label className={labelStyles.ecLabel}>
                <span className={labelStyles.ecLabel__value}>
                  Valor
                  <span className={labelStyles.ecLabel__value__info}>
                    (Valor mínimo: {FORMATED_MIN_PRICE})
                  </span>
                </span>
                <CurrencyInput
                  name="price"
                  placeholder={`Ex: ${FORMATED_MIN_PRICE}`}
                  tabIndex="0"
                  onChange={onChangePrice}
                  onFocus={(e) => onInputFocus(e.target.offsetTop)}
                  value={state.price || null}
                />
              </label>
            )}
            <button className={composesStyles.formhiddenbutton} type="submit">
              enviar
            </button>
          </form>
        </>
      )}
    </div>
  )
}

Listing.propTypes = {
  onAddressFocus: PropTypes.func
}

export default Listing
